import { useState } from 'react';
import HanumanLeapChallenge from '../components/HanumanLeapChallenge';
import ArjunaArrowTrail from '../components/ArjunaArrowTrail';
import ArjunaArcheryChallenge from '../components/ArjunaArcheryChallenge';
import KrishnaButterBall from '../components/KrishnaButterBall';
import Meta from '../components/Meta';
import CarGame from '../components/RathGame';

export default function Games() {
  const [activeGame, setActiveGame] = useState('hanuman');
  
  // Schema markup for structured data
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "VEDA Mythological Games",
    "description": "Fun and educational mythological games for kids based on Indian epics like Ramayana and Mahabharata.",
    "url": "https://vedamerchandise.com/games",
    "publisher": {
      "@type": "Organization",
      "name": "VEDA Merchandise",
      "logo": {
        "@type": "ImageObject",
        "url": "https://vedamerchandise.com/logo.svg"
      }
    }
  };

  return (
    <>
      <Meta 
        title="VEDA Mythological Games - Educational Games for Kids"
        description="Fun and educational mythological games for kids based on Indian epics like Ramayana and Mahabharata."
        keywords="mythological games, Indian mythology games, educational games, kids games, VEDA games, Ramayana games, Mahabharata games"
        author="VEDA Merchandise"
        ogTitle="VEDA Mythological Games - Educational Games for Kids"
        ogDescription="Fun and educational mythological games for kids based on Indian epics like Ramayana and Mahabharata."
        ogImage="https://vedamerchandise.com/games-preview.jpg"
        ogUrl="https://vedamerchandise.com/games"
        ogType="website"
        twitterCard="summary_large_image"
        twitterTitle="VEDA Mythological Games - Educational Games for Kids"
        twitterDescription="Fun and educational mythological games for kids based on Indian epics like Ramayana and Mahabharata."
        twitterImage="https://vedamerchandise.com/games-preview.jpg"
        robots="index, follow"
        canonicalUrl="https://vedamerchandise.com/games"
        schemaMarkup={schemaMarkup}
      />
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-red-50">
        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <h1 className="text-5xl font-bold mb-4 fredoka">
              <span className="text-red-500">Mythological</span>{' '}
              <span className="text-yellow-500">Games</span>
            </h1>
            
            <p className="text-xl text-gray-700 mb-8">
              Fun & Educational Games for Kids
            </p>
          </div>

          {/* Game Selector */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <div className="bg-white rounded-full p-1 shadow-lg">
              <button
                onClick={() => setActiveGame('hanuman')}
                className={`px-6 py-3 rounded-full font-semibold transition-colors ${
                  activeGame === 'hanuman' 
                    ? 'bg-red-500 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Hanuman Leap
              </button>
            </div>
            
            <div className="bg-white rounded-full p-1 shadow-lg">
              <button
                onClick={() => setActiveGame('arjuna')}
                className={`px-6 py-3 rounded-full font-semibold transition-colors ${
                  activeGame === 'arjuna' 
                    ? 'bg-red-500 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Arjuna's Arrow
              </button>
            </div>
            
            <div className="bg-white rounded-full p-1 shadow-lg">
              <button
                onClick={() => setActiveGame('archery')}
                className={`px-6 py-3 rounded-full font-semibold transition-colors ${
                  activeGame === 'archery' 
                    ? 'bg-red-500 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Arjuna's Archery
              </button>
            </div>
            
            <div className="bg-white rounded-full p-1 shadow-lg">
              <button
                onClick={() => setActiveGame('krishna')}
                className={`px-6 py-3 rounded-full font-semibold transition-colors ${
                  activeGame === 'krishna' 
                    ? 'bg-red-500 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Krishna's Challenge
              </button>
            </div>

            <div className="bg-white rounded-full p-1 shadow-lg">
              <button
                onClick={() => setActiveGame('Rath')}
                className={`px-6 py-3 rounded-full font-semibold transition-colors ${
                  activeGame === 'Rath' 
                    ? 'bg-red-500 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Rath 
              </button>
            </div>
          
          </div>

          {/* Game Content */}
          <div className="bg-white rounded-2xl shadow-xl p-4 md:p-8 mb-12 min-h-[500px]">
            {activeGame === 'hanuman' && <HanumanLeapChallenge />}
            {activeGame === 'arjuna' && <ArjunaArrowTrail />}
            {activeGame === 'archery' && <ArjunaArcheryChallenge />}
            {activeGame === 'krishna' && <KrishnaButterBall />}
            {activeGame === 'Rath' && <CarGame />}
          </div>

          {/* Game Descriptions */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
            <div className="bg-gradient-to-br from-red-50 to-yellow-50 rounded-2xl p-6 shadow-md">
              <h3 className="text-2xl font-bold text-red-600 mb-3 fredoka">Hanuman Leap</h3>
              <p className="text-gray-700 mb-4">
                Help Hanuman leap across the ocean to Lanka in this exciting endless runner!
                Collect Sanjeevani herbs and avoid demon obstacles.
              </p>
              <ul className="text-gray-600 list-disc pl-5 space-y-1">
                <li>Endless runner gameplay</li>
                <li>Based on Hanuman's legendary leap</li>
                <li>Collect power-ups for extra points</li>
                <li>Engaging for kids and adults alike</li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-red-50 to-yellow-50 rounded-2xl p-6 shadow-md">
              <h3 className="text-2xl font-bold text-red-600 mb-3 fredoka">Arjuna's Arrow</h3>
              <p className="text-gray-700 mb-4">
                Guide Arjuna's divine arrow through an aerial maze of obstacles!
                Collect Focus Orbs and power-ups to maintain the divine glow.
              </p>
              <ul className="text-gray-600 list-disc pl-5 space-y-1">
                <li>Precision navigation gameplay</li>
                <li>Based on Arjuna's divine archery</li>
                <li>Multiple power-ups and abilities</li>
                <li>Challenging for older kids</li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-red-50 to-yellow-50 rounded-2xl p-6 shadow-md">
              <h3 className="text-2xl font-bold text-red-600 mb-3 fredoka">Arjuna's Archery</h3>
              <p className="text-gray-700 mb-4">
                Master the divine art of archery with Arjuna through Guru Dronacharya's trials.
                Each level teaches focus, patience, and precision.
              </p>
              <ul className="text-gray-600 list-disc pl-5 space-y-1">
                <li>5 challenging levels with unique mechanics</li>
                <li>Realistic physics and wind effects</li>
                <li>Educational storyline from Mahabharata</li>
                <li>Wisdom quotes and character development</li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-red-50 to-yellow-50 rounded-2xl p-6 shadow-md">
              <h3 className="text-2xl font-bold text-red-600 mb-3 fredoka">Krishna's Challenge</h3>
              <p className="text-gray-700 mb-4">
                Help baby Krishna collect butter balls while avoiding broken pots in this fun adventure game!
                Learn about Krishna's playful childhood stories.
              </p>
              <ul className="text-gray-600 list-disc pl-5 space-y-1">
                <li>Control Krishna with arrow keys or touch</li>
                <li>Collect butter balls for points</li>
                <li>Avoid obstacles to preserve lives</li>
                <li>Progress through 3 levels of increasing difficulty</li>
              </ul>
            </div>
          
          </div>

          {/* Educational Value */}
          <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-2xl p-8 text-white text-center shadow-xl mb-12">
            <h2 className="text-3xl font-bold mb-4 fredoka">Why Learn Mythology Through Games?</h2>
            <p className="text-xl max-w-3xl mx-auto">
              Indian mythology is filled with wisdom, values, and fascinating stories that have been passed down for generations. 
              Our games make learning about these stories fun and engaging for children while developing their focus and concentration!
            </p>
          </div>
        </main>
      </div>
    </>
  );
}